import { Button } from '@mui/material';
import React, { FC } from 'react';

import { Link, useLocation } from 'react-router-dom';

import useComposeType from '../../../hooks/router/search/useComposeType';

import translate from '../../../i18n/translate';
import { DialogId } from '../../../models/dialog';
import { getOpenComposeUrl } from '../../../routes/urls/searchParams/compose';
import ConfirmPopover from '../../MUI/ConfirmPopover';
import MuiIcon from '../../MUI/MuiIcon';

import * as Styled from './styled';

type Props = {
  requestDiscard: () => void;
};

/**
 * Minimized draft for compose on mobile
 *
 * @param props                Props passed to the component
 * @param props.requestDiscard Callback for discarding the form
 *
 * @returns                    The component itself
 */
const ComposeDraft: FC<Props> = ({ requestDiscard }) => {
  const composeType = useComposeType();
  const location = useLocation();

  if (composeType === null) {
    return null;
  }

  const continueUrl = getOpenComposeUrl(location, composeType);

  const isPost = composeType === 'post';
  const title = translate(
    isPost ? 'GENERAL__POST_DRAFT' : 'GENERAL__TASK_DRAFT',
  );

  return (
    <Styled.DraftWrapper>
      <Styled.DraftTypeIndicator data-is-post={isPost} />
      <Styled.Header>
        <MuiIcon icon={composeType} />
        <Styled.DraftTitle title={title}>{title}</Styled.DraftTitle>
      </Styled.Header>
      <Styled.Footer>
        <Button component={Link} to={continueUrl} variant="outlined">
          {translate('GENERAL__CONTINUE')}
        </Button>
        <ConfirmPopover
          onConfirm={requestDiscard}
          popoverProps={{
            anchorOrigin: {
              horizontal: 'right',
              vertical: 'top',
            },
            slotProps: {
              paper: {
                elevation: 5,
                sx: Styled.PopoverStyles,
              },
            },
            transformOrigin: {
              horizontal: 'right',
              vertical: 'bottom',
            },
          }}
          trigger={
            <Styled.DiscardButton
              startIcon={<MuiIcon icon="deleteOutlined" />}
              variant="outlined"
            >
              {translate('GENERAL__DISCARD')}
            </Styled.DiscardButton>
          }
          type={DialogId.ComposeClose}
        />
      </Styled.Footer>
    </Styled.DraftWrapper>
  );
};

export default ComposeDraft;
